import { useState } from "react";
import {
  ArrowUpRight,
  Compass,
  Shield,
  Layers,
  Award,
  Building2,
  HeartHandshake,
  Database,
  Workflow,
  Network,
  BarChart3,
  FileText,
  Users,
  Bell,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import LeadModal from "../../components/public/LeadModal";
import { IconVisual } from "./MarketingHome";

const PILLARS = [
  {
    icon: Layers,
    title: "Infrastructure",
    copy: "Production-grade ingestion across Karvy + CAMS. Reconciled at folio, family, and firm level.",
  },
  {
    icon: Compass,
    title: "Independence",
    copy: "Your brand, your relationships. Kinntegra is the layer underneath — never above — your practice.",
  },
  {
    icon: Shield,
    title: "Continuity",
    copy: "Every field change audit-logged. Nothing ever lost — practice value survives transitions.",
  },
  {
    icon: Award,
    title: "Enterprise Thinking",
    copy: "Multi-associate workflows, scoped books, attribution — designed for firms, not solo operators.",
  },
];

export default function About() {
  const [leadOpen, setLeadOpen] = useState(false);
  return (
    <PublicLayout title="About">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full bg-[#C8A96B]/10 blur-[140px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 md:pt-28 pb-12">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
            <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
            About Kinntegra
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-light tracking-[-0.025em] leading-[1.05] max-w-4xl">
            We are building the layer{" "}
            <span className="italic text-[#C8A96B]">
              independent MFDs deserve.
            </span>
          </h1>
          <p className="mt-8 text-lg text-white/65 max-w-3xl font-light leading-relaxed">
            Indian Mutual Fund Distributors built one of the largest
            retail wealth ecosystems in the world — and most still run
            their businesses on tools designed for someone else's umbrella.
            We are here to change that.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section
        id="mission"
        className="py-20 md:py-28 border-y border-white/5"
        data-testid="about-mission"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <IconVisual
              items={[
                { icon: Building2, label: "Infrastructure" },
                { icon: Database, label: "Reconciled Data" },
                { icon: Layers, label: "Lot-level" },
                { icon: Workflow, label: "Workflows" },
                { icon: Network, label: "Multi-broker" },
                { icon: BarChart3, label: "Reporting" },
              ]}
              eyebrow="Institutional-grade"
              caption="Production-ready infrastructure designed for distribution practices that intend to last."
              testId="about-mission-image"
              ratio="aspect-[5/6]"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-white/40">
              Our mission
            </div>
            <p className="mt-6 text-2xl md:text-3xl font-light leading-[1.4] text-white/90 tracking-tight">
              To equip independent MFDs with institutional-grade
              infrastructure — without compromising the independence,
              identity, and client ownership that built the practice.
            </p>
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section
        className="py-20 md:py-28 border-b border-white/5"
        data-testid="about-pillars"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
            <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
            What we believe
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-[-0.02em] max-w-3xl leading-[1.15]">
            Four principles guide every decision.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="bg-[#07111F] p-10 hover:bg-[#0c1827] transition group"
                data-testid={`about-pillar-${p.title.toLowerCase()}`}
              >
                <p.icon
                  size={24}
                  className="text-[#C8A96B] mb-8"
                  strokeWidth={1.4}
                />
                <h3 className="text-2xl font-light text-white tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm text-white/55 leading-relaxed max-w-md">
                  {p.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Succession */}
      <section
        id="succession"
        className="py-16 md:py-20 border-b border-white/5"
        data-testid="about-succession"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
              <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
              Succession
            </div>
            <h2 className="mt-6 text-3xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.1]">
              You spent years building trust.
              <br />
              <span className="italic text-[#C8A96B]">
                Kinntegra ensures it survives.
              </span>
            </h2>
            <p className="mt-8 text-lg text-white/60 font-light leading-relaxed max-w-xl">
              Continuity isn't a feature. It's the philosophy. From
              field-level audit logs to multi-associate handover workflows,
              every part of Kinntegra is designed so that the practice you
              built outlives the moment.
            </p>
          </div>
          <div className="lg:col-span-5">
            <IconVisual
              items={[
                { icon: HeartHandshake, label: "Continuity" },
                { icon: FileText, label: "Audit logs" },
                { icon: Users, label: "Handover" },
                { icon: Bell, label: "Alerts" },
                { icon: RefreshCcw, label: "Versioning" },
                { icon: ShieldCheck, label: "Succession" },
              ]}
              eyebrow="Across generations"
              caption="Built to carry a practice — and the relationships inside it — through every transition."
              testId="about-succession-image"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-light tracking-[-0.02em] leading-[1.15]">
            Ready to see what comes after reporting?
          </h2>
          <div className="mt-12 flex justify-center">
            <button
              onClick={() => setLeadOpen(true)}
              data-testid="about-cta-getstarted"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
            >
              Speak to Our Team
              <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </section>
      {leadOpen && (
        <LeadModal onClose={() => setLeadOpen(false)} source="about_page" />
      )}
    </PublicLayout>
  );
}
